const UINT32_RANGE = 0x1_0000_0000;
const NON_ZERO_FALLBACK = 0x6d2b_79f5;

/**
 * Serializable state for a deterministic xorshift32 stream. Consumers own
 * their state explicitly; unrelated streams must never share mutable state.
 */
export interface SeededRandomState {
  readonly algorithm: "xorshift32";
  readonly seed: string;
  readonly value: number;
  readonly draws: number;
}

function seedStringToUint32(seed: string): number {
  let hash = 0x811c_9dc5;

  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index);
    hash = Math.imul(hash, 0x0100_0193);
  }

  const unsigned = hash >>> 0;
  return unsigned === 0 ? NON_ZERO_FALLBACK : unsigned;
}

export function createSeededRandom(seed: string): SeededRandomState {
  return {
    algorithm: "xorshift32",
    seed,
    value: seedStringToUint32(seed),
    draws: 0,
  };
}

export interface RandomDraw<T> {
  readonly value: T;
  readonly state: SeededRandomState;
}

export function nextUint32(state: SeededRandomState): RandomDraw<number> {
  let value = state.value >>> 0;
  value ^= value << 13;
  value ^= value >>> 17;
  value ^= value << 5;
  value >>>= 0;

  return {
    value,
    state: {
      ...state,
      value,
      draws: state.draws + 1,
    },
  };
}

export function nextRandomFloat(state: SeededRandomState): RandomDraw<number> {
  const draw = nextUint32(state);
  return {
    value: draw.value / UINT32_RANGE,
    state: draw.state,
  };
}

export function nextRandomInt(
  state: SeededRandomState,
  maximumExclusive: number,
): RandomDraw<number> {
  if (
    !Number.isSafeInteger(maximumExclusive) ||
    maximumExclusive <= 0 ||
    maximumExclusive > UINT32_RANGE
  ) {
    throw new RangeError("maximumExclusive must be a positive safe integer no greater than 2^32");
  }

  const acceptanceLimit = Math.floor(UINT32_RANGE / maximumExclusive) * maximumExclusive;
  let currentState = state;

  for (;;) {
    const draw = nextUint32(currentState);
    currentState = draw.state;
    if (draw.value < acceptanceLimit) {
      return {
        value: draw.value % maximumExclusive,
        state: currentState,
      };
    }
  }
}

export function shuffleDeterministically<T>(
  values: readonly T[],
  state: SeededRandomState,
): RandomDraw<readonly T[]> {
  const shuffled = [...values];
  let currentState = state;

  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const draw = nextRandomInt(currentState, index + 1);
    currentState = draw.state;
    const selected = draw.value;
    const temporary = shuffled[index] as T;
    shuffled[index] = shuffled[selected] as T;
    shuffled[selected] = temporary;
  }

  return { value: shuffled, state: currentState };
}
